import React from "react";
import PropTypes from "prop-types";
import R from "ramda";
import styles from "components/documents/document-filters.scss";
import FaSearch from "react-icons/fa/search";
import FaPlus from "react-icons/fa/plus";
import {
  cardNumberType,
  categoryType,
  currencyType,
  envelopeType,
  momentType,
  vendorType
} from "custom-prop-types";
import { onEnterPressed } from "utils/key-press";
import { Select, SimpleSelect } from "components/common/select";
import { DateRange } from "components/common/date-range";
import { PaymentTypeFilterField } from "components/documents/filters/payment-type-filter-field";
import { ActiveFilters } from "./active-filters";
import { ExportTypeSelector } from "./export-type-selector";
import { DocumentTypeSelector } from "./document-type-selector";
import { TotalRange } from "./total-range";

export const DocumentFilters = ({
  cardNumbers,
  categories,
  currencies,
  endDate,
  envelopes,
  exportType,
  isExports,
  lastFourDigits,
  name,
  onAddCardNumber,
  onAddCategory,
  onAddCurrency,
  onAddDocumentType,
  onAddEndDate,
  onAddEnvelope,
  onAddSearchQuery,
  onAddNameToFilter,
  onAddPaymentType,
  onAddStartDate,
  onAddTotalFilter,
  onAddVendor,
  onAddViewedStatus,
  onClearExportType,
  onRemoveCardNumber,
  onRemoveCategory,
  onRemoveCurrency,
  onRemoveDocumentType,
  onRemoveEnvelope,
  onRemoveSearchQuery,
  onRemoveNameFromFilter,
  onRemovePaymentType,
  onRemoveVendor,
  onRemoveViewedStatus,
  onSetExportType,
  onUpdateName,
  onUpdateSearchQuery,
  onUpdateTotalFilter,
  preferredCurrency,
  searchQueryString,
  selectedCardNumbers,
  selectedCategories,
  selectedCurrencies,
  selectedEnvelopes,
  selectedPaymentTypes,
  selectedName,
  selectedSearchQueryString,
  selectedVendors,
  startDate,
  totalMax,
  totalMin,
  type,
  vendors,
  viewedStatus
}) => (
  <div className={ styles.container }>
    <h3 className={ styles.title }>Filter by:</h3>
    <div className={ styles["field-cell"] }>
      <DocumentTypeSelector onChange={ onAddDocumentType } />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Vendor</span>
      <Select data={ mapVendors(vendors) } onChange={ onAddVendor } placeholder="Select a vendor..." />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Document name</span>
      <div className={ styles.search }>
        <input
          className={ styles.fields }
          onChange={ (e) => onUpdateName(e.target.value) }
          onKeyDown={ (e) => onEnterPressed(e, name, onAddNameToFilter) }
          placeholder={ "Add a document name" }
          value={ name }
        />
        <FaPlus className={ styles["plus-button"] } onClick={ () => onAddNameToFilter(name) } />
      </div>
    </div>
    <div className={ styles["field-cell"] }>
      <span>Category</span>
      <Select data={ mapCategories(categories) } onChange={ onAddCategory } placeholder="Select a category..." />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Total</span>
      <TotalRange
        onAddTotalFilter={ onAddTotalFilter }
        onUpdateTotalFilter={ onUpdateTotalFilter }
        totalMax={ totalMax }
        totalMin={ totalMin }
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Date Range</span>
      <DateRange
        currency={ preferredCurrency }
        endDate={ endDate }
        onAddEndDate={ onAddEndDate }
        onAddStartDate={ onAddStartDate }
        startDate={ startDate }
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Envelope</span>
      <Select
        data={ mapEnvelopes(envelopes) }
        onChange={ onAddEnvelope }
        placeholder="Select an envelope..."
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Currency</span>
      <SimpleSelect
        data={ mapCurrencies(currencies) }
        onChange={ onAddCurrency }
        placeholder="Select a currency..."
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Viewed</span>
      <SimpleSelect
        data={ viewedArray }
        onChange={ onAddViewedStatus }
        placeholder="Select viewed status..."
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Search</span>
      <div className={ styles.search }>
        <input
          className={ styles.fields }
          onChange={ (e) => onUpdateSearchQuery(e.target.value) }
          onKeyDown={ (e) => onEnterPressed(e, searchQueryString, onAddSearchQuery) }
          placeholder={ "Search for documents here" }
          value={ searchQueryString }
        />
        <FaSearch className={ styles["search-button"] } onClick={ () => onAddSearchQuery(searchQueryString) } />
      </div>
    </div>
    <div className={ styles["field-cell"] }>
      <span>Export Type</span>
      <ExportTypeSelector categories={ categories } onChange={ onSetExportType } />
    </div>
    <div className={ `${styles["field-cell"]} ${styles["payment-type-container"]}` }>
      <div className={ styles["payment-type"] }>
        <PaymentTypeFilterField
          cardNumbers={ cardNumbers }
          lastFourDigits={ lastFourDigits }
          onAddCardNumber={ onAddCardNumber }
          onAddPaymentType={ onAddPaymentType }
          selectedPaymentTypes={ selectedPaymentTypes }
        />
      </div>
    </div>
    <ActiveFilters
      exportType={ exportType }
      isExports={ isExports }
      onClearExportType={ onClearExportType }
      onRemoveCardNumber={ onRemoveCardNumber }
      onRemoveCategory={ onRemoveCategory }
      onRemoveCurrency={ onRemoveCurrency }
      onRemoveDocumentType={ onRemoveDocumentType }
      onRemoveEnvelope={ onRemoveEnvelope }
      onRemoveNameFromFilter={ onRemoveNameFromFilter }
      onRemovePaymentType={ onRemovePaymentType }
      onRemoveSearchQuery={ onRemoveSearchQuery }
      onRemoveVendor={ onRemoveVendor }
      onRemoveViewedStatus={ onRemoveViewedStatus }
      searchQueryString={ selectedSearchQueryString }
      selectedCardNumbers={ selectedCardNumbers }
      selectedCategories={ selectedCategories }
      selectedCurrencies={ selectedCurrencies }
      selectedDocumentType={ type }
      selectedEnvelopes={ selectedEnvelopes }
      selectedName={ selectedName }
      selectedPaymentTypes={ selectedPaymentTypes }
      selectedVendors={ selectedVendors }
      viewedStatus={ viewedStatus }
    />
  </div>
);

DocumentFilters.defaultProps = {
  endDate: null,
  exportType: null,
  lastFourDigits: null,
  name: "",
  selectedSearchQueryString: null,
  startDate: null,
  totalMax: "",
  totalMin: "",
  type: null
};

DocumentFilters.propTypes = {
  cardNumbers: PropTypes.arrayOf(cardNumberType).isRequired,
  categories: PropTypes.arrayOf(categoryType).isRequired,
  currencies: PropTypes.arrayOf(currencyType).isRequired,
  endDate: momentType,
  envelopes: PropTypes.arrayOf(envelopeType).isRequired,
  exportType: PropTypes.string,
  isExports: PropTypes.bool.isRequired,
  lastFourDigits: PropTypes.string,
  name: PropTypes.string,
  onAddCardNumber: PropTypes.func.isRequired, // (string) => void
  onAddCategory: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddCurrency: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddDocumentType: PropTypes.func.isRequired,
  onAddEndDate: PropTypes.func.isRequired,
  onAddEnvelope: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddNameToFilter: PropTypes.func.isRequired,
  onAddPaymentType: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddSearchQuery: PropTypes.func.isRequired,
  onAddStartDate: PropTypes.func.isRequired,
  onAddTotalFilter: PropTypes.func.isRequired,
  onAddVendor: PropTypes.func.isRequired, // ({key: string, name: string}) => void
  onAddViewedStatus: PropTypes.func.isRequired,
  onClearExportType: PropTypes.func.isRequired, // () => void
  onRemoveCardNumber: PropTypes.func.isRequired,
  onRemoveCategory: PropTypes.func.isRequired, // (string) => void
  onRemoveCurrency: PropTypes.func.isRequired, // (string) => void
  onRemoveDocumentType: PropTypes.func.isRequired,
  onRemoveEnvelope: PropTypes.func.isRequired, // (string) => void
  onRemoveNameFromFilter: PropTypes.func.isRequired,
  onRemovePaymentType: PropTypes.func.isRequired, // (string) => void
  onRemoveSearchQuery: PropTypes.func.isRequired,
  onRemoveVendor: PropTypes.func.isRequired, // (string) => void
  onRemoveViewedStatus: PropTypes.func.isRequired,
  onSetExportType: PropTypes.func.isRequired, // (string) => void
  onUpdateName: PropTypes.func.isRequired,
  onUpdateSearchQuery: PropTypes.func.isRequired,
  onUpdateTotalFilter: PropTypes.func.isRequired,
  preferredCurrency: PropTypes.string.isRequired,
  searchQueryString: PropTypes.string.isRequired,
  selectedCardNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCurrencies: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedEnvelopes: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedName: PropTypes.string.isRequired,
  selectedPaymentTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSearchQueryString: PropTypes.string,
  selectedVendors: PropTypes.arrayOf(PropTypes.string).isRequired,
  startDate: momentType,
  totalMax: PropTypes.string,
  totalMin: PropTypes.string,
  type: PropTypes.string,
  vendors: PropTypes.arrayOf(vendorType).isRequired,
  viewedStatus: PropTypes.string.isRequired
};

const mapEnvelopes = (envelopes) => {
  const newEnvelopes = envelopes.map(envelope => ({ key: envelope.code, name: envelope.code }));
  return R.prepend({ key: "none", name: "No Envelopes" }, newEnvelopes);
};
const mapCategories = (categories) => {
  const newCategories = categories.map(category => ({ key: category.name, name: category.name }));
  return R.prepend({ key: "none", name: "No category" }, newCategories);
};
const mapCurrencies = (currencies) => currencies.map(currency => ({ key: currency.code, name: currency.code }));
const mapVendors = (vendors) => vendors.map(vendor => ({ key: vendor.name, name: vendor.name }));

const viewedArray = [
  {
    key: "viewed",
    name: "Viewed"
  },
  {
    key: "unviewed",
    name: "Unviewed"
  }
];



// WEBPACK FOOTER //
// ./src/js/components/documents/filters/document-filters.jsx